/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
  KeyboardAvoidingView,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import { createApiClient, apiClient } from "../../../api/apiClient";
import LHDataTable from "../../../components/LHDataTable";
import LHFilter from "../../../components/LHFilter";
import LhDatePicker from "../../../components/LhDatePicker";
import LhToggleSwitch from "../../../components/LhToggleSwitch";
import { FilterItem } from "../../../types/observation";
import {
  EmployeeUpdatesHeader,
  EmployeeEditOverlay,
  EmployeeFormSection,
  ToggleSwitches,
} from "../components/EmployeeUpdates";
import EmployeeCardItem, { CardSeparator } from "../components/EmployeeUpdates/EmployeeCardItem";
import LocationRequiredMessage from "../components/EmployeeUpdates/LocationRequiredMessage";
import LoadingMessage from "../components/EmployeeUpdates/LoadingMessage";
import NoResultsMessage from "../components/EmployeeUpdates/NoResultsMessage";
import { setEmployees } from "../../../store/slices/appDataSlice";
import MobileFilterOverlay from "../../../components/MobileFilterOverlay";

import { createCleanEmployeeData } from "../components/EmployeeUpdates/utils";
import {
  deleteEmployee,
  updateEmployee,
  setEditEmployee,
  setShowOverlay,
  updateFilteredEmployeeList,
  setActiveFilter,
  clearSuccessMessage,
  fetchEmployees,
  clearError,
  setShowPeriodObservation,
  setShowWeeklyPulseCheck,
} from "../../../store/slices/employeeUpdatesSlice";

// Import Employee type from the Redux slice
import type { Employee } from "../../../store/slices/employeeUpdatesSlice";
import LhDatePickerFilter from "../../../components/LhDatePickerFilter";
import { formatUserName } from "../../../utils/emailTouserName";
import FontFamily from "../../../constants/fonts";
import AdminBreadcrumb from "../../Admin/components/AdminBreadcrumb";
import { useRefreshRoles } from "../../../hooks";
import { useTheme } from "../../../theme";

// Map our filter keys to API parameter keys
const API_FILTER_KEY_MAP: Record<string, string> = {
  fullName: "Full_Name", // Will be split into First_Name and Last_Name
  gpid: "GPID",
  status: "Active_vs_Inactive",
  jobDescription: "Role",
  manager: "Manager",
  lastObservation: "Last_Observation",
  lastPulseCheck: "Last_Pulse_Check",
  hireDate: "Hire_Date",
  graduationDate: "Expected_Graduation_Date",
  newHire: "New_Hire",
};

const statusOptions = [
  { id: "1", displayText: "Active" },
  { id: "2", displayText: "Inactive" },
  { id: "3", displayText: "Terminated" },
];

interface ManageEmployeesProps {
  onBackPress?: () => void;
  onEmployeeProfileClick?: (employeeId: string) => void;
}

const ManageEmployeeUpdates: React.FC<ManageEmployeesProps> = ({
  onBackPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const [metricValue, setMetricValue] = useState("DDS / SIA / Safety");

  // Mobile pagination state for iOS card view
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10; // Show 10 records per mobile page
  const [displayStart, setDisplayStart] = useState(1); // Window start for pagination

  // Get state from Redux
  const dispatch = useDispatch<AppDispatch>();
  const {
    employeeList,
    showOverlay,
    editEmployee,
    showPeriodObservation,
    showWeeklyPulseCheck,
    filteredEmployeeList,
    activeFilters,
    successMessage,
    loading,
    error,
  } = useSelector((state: any) => state.employeeUpdates);

  // Form state is now handled by the EmployeeFormSection component directly

  // State to track if add employee overlay is visible on iPhone
  const [isAddEmployeeOverlayVisible, setIsAddEmployeeOverlayVisible] = useState(false);

  // State to track if filter modal is visible on iPhone
  const [showFilterModal, setShowFilterModal] = useState(false);
  
  // Temporary filter state for mobile overlay (following QualityAuditFilterModal pattern)
  const [tempFilters, setTempFilters] = useState<any>({});

  // Get selected location from appData (roles still needed for job description filter)
  const { selectedLocation, roles: appDataRoles, employees: appDataEmployees } = useSelector(
    (state: any) => state.appData
  );

  // Refresh roles on every mount so recent admin changes are reflected in dropdowns
  useRefreshRoles();

  // Compute iOS mobile boolean inside the component
  const windowWidth = Dimensions.get("window").width;
  const isMobileIos = Platform.OS === "ios" && windowWidth < 768;

  // Reset to first page whenever the filtered list changes
  useEffect(() => {
    setCurrentPage(1);
    setDisplayStart(1);
  }, [filteredEmployeeList]);

  // Ensure currentPage does not exceed total pages when data shrinks
  const totalPages = Math.max(
    1,
    Math.ceil((filteredEmployeeList?.length || 0) / rowsPerPage)
  );
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Fetch employees when component mounts or when location changes or filters change
  useEffect(() => {
    if (selectedLocation?.id) {
      const filters: Record<string, string> = {};
      
      if (showPeriodObservation) {
        filters.periodObservation = "true";
      }
      
      if (showWeeklyPulseCheck) {
        filters.pulseCheck = "true";
      }

      const hasSpecialFilters = Object.keys(filters).length > 0;
      const alreadyLoaded = Array.isArray(employeeList) && employeeList.length > 0;

      // Skip fetch if data is already in the store and no special filters are needed
      if (alreadyLoaded && !hasSpecialFilters) {
        return;
      }
      
      dispatch(fetchEmployees({ 
        location: selectedLocation.displayText,
        filters: hasSpecialFilters ? filters : undefined 
      }));
    }
  }, [dispatch, selectedLocation, showPeriodObservation, showWeeklyPulseCheck]);

  // Debug appData changes (roles still needed from appData)
  useEffect(() => {
    console.log(
      "Using employeeList for filter options (from employeeUpdates slice):",
      employeeList
    );
    console.log("AppData roles changed:", appDataRoles);
    console.log("AppData roles structure:", appDataRoles?.roles);
    console.log("AppData employees updated:", 
      Array.isArray(appDataEmployees) ? appDataEmployees.length : appDataEmployees?.data?.length || 0, 
      "employees");
  }, [employeeList, appDataRoles, appDataEmployees]);

  // Initialize filtered employee list when component mounts
  useEffect(() => {
    dispatch(updateFilteredEmployeeList());
  }, [dispatch, employeeList]);

  // Remove the effect that updates filtered data when active filters change
  // This was causing automatic filtering - we only want filtering on button click
  // useEffect(() => {
  //   dispatch(updateFilteredEmployeeList());
  // }, [dispatch, activeFilters]);

  // Add ref for ScrollView to enable auto-scroll to top
  const scrollViewRef = useRef<ScrollView>(null);

  // Add state for notification messages
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Handle success message with auto-clear timer and auto-scroll
  useEffect(() => {
    if (successMessage) {
      // Scroll to top to show the success message
      scrollViewRef.current?.scrollTo({
        y: 0,
        animated: true,
      });

      // Auto-clear the success message after 5 seconds
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
      }, 5000); // Clear after 5 seconds

      // Cleanup timer on unmount or when dependencies change
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Handle error messages with notification bar
  useEffect(() => {
    if (error) {
      setErrorMessage(
        `Error Loading Data: ${error}. The current data will continue to be displayed.`
      );

      // Clear the error message after 5 seconds
      setTimeout(() => {
        dispatch(clearError());
        setErrorMessage(""); // Also clear local state
      }, 5000);
    }
  }, [error, dispatch]);

  // Clear error message when error is cleared from Redux
  useEffect(() => {
    if (!error) {
      setErrorMessage("");
    }
  }, [error]);

  // Refresh the filtered list when the overlay closes
  useEffect(() => {
    if (!showOverlay) {
      // Update the filtered employee list to reflect any changes
      dispatch(updateFilteredEmployeeList());

      // Reset the editEmployee state when the overlay is closed
      dispatch(setEditEmployee(null));
    }
  }, [showOverlay, dispatch]);

  // Helper function to build filters object from active filters
  // Maps filter values to API parameters:
  // - fullName -> First_Name + Last_Name (by finding employee in employeeList)
  // - gpid -> GPID (direct value)
  // - status -> Active_vs_Inactive (direct value)
  // - jobDescription -> Role (by finding role name from appDataRoles)
  // - hireDate -> Hire_Date (ISO format: "2024-10-31T00:00:00.000Z")
  // - graduationDate -> Expected_Graduation_Date (ISO format)
  // - newHire -> New_Hire (boolean as string: "true"/"false")
  // - manager -> Manager (direct value)
  // - lastObservation -> Last_Observation (ISO format)
  // - lastPulseCheck -> Last_Pulse_Check (ISO format)
  const buildFiltersObject = (excludeKey?: string): Record<string, string> => {
    const filters: Record<string, string> = {};

    console.log('[BUILD FILTERS DEBUG] Starting buildFiltersObject with activeFilters:', activeFilters);

    // Add active filters to the filters object (except for the one being excluded)
    Object.entries(activeFilters).forEach(([filterKey, filterValue]) => {
      if (
        filterValue !== null &&
        filterValue !== undefined &&
        filterValue !== "" &&
        filterKey !== excludeKey
      ) {
        console.log(`[BUILD FILTERS DEBUG] Processing ${filterKey}:`, filterValue);
        
        // Handle Full Name filter - split into First_Name and Last_Name
        if (filterKey === "fullName") {
          // Find the employee by the full name to get First_Name and Last_Name
          const selectedEmployee = employeeList?.find(
            (emp: Employee) => {
              const empFullName = `${emp.firstName} ${emp.lastName}`;
              console.log(`[BUILD FILTERS DEBUG] Comparing "${empFullName}" with "${filterValue}"`);
              return empFullName === filterValue;
            }
          );
          console.log('[BUILD FILTERS DEBUG] Found employee for fullName:', selectedEmployee);
          if (selectedEmployee) {
            filters.First_Name = selectedEmployee.firstName;
            filters.Last_Name = selectedEmployee.lastName;
            console.log(`[BUILD FILTERS DEBUG] Added First_Name: ${filters.First_Name}, Last_Name: ${filters.Last_Name}`);
          } else {
            console.warn('[BUILD FILTERS DEBUG] No employee found matching fullName:', filterValue);
            // Try to split the name manually as fallback
            const nameParts = String(filterValue).trim().split(/\s+/);
            if (nameParts.length >= 2) {
              filters.First_Name = nameParts[0];
              filters.Last_Name = nameParts.slice(1).join(' ');
              console.log(`[BUILD FILTERS DEBUG] Using fallback split - First_Name: ${filters.First_Name}, Last_Name: ${filters.Last_Name}`);
            }
          }
        }

        // Handle Job Description filter - map to Role using role ID
        else if (filterKey === "jobDescription") {
          console.log('[BUILD FILTERS DEBUG] Processing jobDescription:', filterValue);
          // Find the role name from appDataRoles using the selected role ID
          const selectedRole = appDataRoles?.find(
            (role: { ROLE_ID: number | string; ROLE_NAME: string }) =>
              role.ROLE_ID.toString() === filterValue ||
              role.ROLE_NAME === filterValue
          );
          console.log('[BUILD FILTERS DEBUG] Found role:', selectedRole);
          if (selectedRole) {
            filters.Role = selectedRole.ROLE_NAME;
            console.log('[BUILD FILTERS DEBUG] Added Role:', filters.Role);
          }
        }

        // Handle GPID filter - use the GPID value directly
        else if (filterKey === "gpid") {
          filters.GPID = String(filterValue);
          console.log('[BUILD FILTERS DEBUG] Added GPID:', filters.GPID);
        }

        // Handle Status filter - map to Active_vs_Inactive
        else if (filterKey === "status") {
          filters.Active_vs_Inactive = String(filterValue);
          console.log('[BUILD FILTERS DEBUG] Added Active_vs_Inactive:', filters.Active_vs_Inactive);
        }

        // Handle Manager filter - use email/id directly
        else if (filterKey === "manager") {
          filters.Manager = String(filterValue);
          console.log('[BUILD FILTERS DEBUG] Added Manager:', filters.Manager);
        }

        // Handle New Hire filter - convert to boolean string
        else if (filterKey === "newHire") {
          if (filterValue === "yes") {
            filters.New_Hire = "true";
          } else if (filterValue === "no") {
            filters.New_Hire = "false";
          }
        }

        // Handle date filters - ensure proper ISO format
        else if (
          [
            "hireDate",
            "graduationDate",
            "lastObservation",
            "lastPulseCheck",
          ].includes(filterKey)
        ) {
          const apiKey = API_FILTER_KEY_MAP[filterKey] || filterKey;
          console.log(
            `Processing date filter ${filterKey} with value:`,
            filterValue
          );

          // Ensure date is in ISO format like "2024-10-31T00:00:00.000Z"
          try {
            const date = new Date(String(filterValue));
            if (!isNaN(date.getTime())) {
              filters[apiKey] = date.toISOString();
              console.log(
                `Date filter ${filterKey} converted to ISO:`,
                filters[apiKey]
              );
            } else {
              console.warn(
                `Invalid date format for ${filterKey}:`,
                filterValue
              );
              filters[apiKey] = String(filterValue);
            }
          } catch (error) {
            console.warn(`Error parsing date for ${filterKey}:`, filterValue);
            filters[apiKey] = String(filterValue);
          }
        }

        // Handle other filters normally
        else {
          const apiKey = API_FILTER_KEY_MAP[filterKey] || filterKey;
          filters[apiKey] = String(filterValue);
          console.log(`[BUILD FILTERS DEBUG] Added other filter ${apiKey}:`, filters[apiKey]);
        }
      } else {
        console.log(`[BUILD FILTERS DEBUG] Skipping ${filterKey} - value is null/undefined/empty or excluded`);
      }
    });

    console.log('[BUILD FILTERS DEBUG] Final built filters object:', filters);
    return filters;
  };

  // Function to fetch employees with applied filters
  const fetchFilteredEmployees = (filters?: Record<string, string>) => {
    if (!selectedLocation?.displayText) {
      alert("Please select a location before applying filters");
      return;
    }

    // Keep the current data displayed even if the API request fails
    dispatch(
      fetchEmployees({
        location: selectedLocation.displayText,
        filters,
      })
    ).catch((error) => {
      console.error("Failed to fetch employees:", error);
      // Error notification bar will be shown automatically due to the error state
    });
  };

  // Filter change handler - automatically applies filters when changed
  const handleFilterChange = (key: string, value: any) => {
    console.log(`Filter Changed: ${key}`, value);

    // Check if location is selected before applying filters
    if (!selectedLocation?.id) {
      alert("Please select a location before applying filters");
      return;
    }

    // Handle date filters (direct string values)
    if (
      [
        "hireDate",
        "graduationDate",
        "lastObservation",
        "lastPulseCheck",
      ].includes(key)
    ) {
      // For date filters, value is either a string (ISO date) or null
      console.log(`Setting date filter ${key} to:`, value);
      dispatch(setActiveFilter({ key, value: value }));
    }
    // Handle dropdown filters (object with id and displayText)
    else {
      if (key === "manager" && value && value.id) {
        // For manager dropdown, store the email (id) in activeFilters
        dispatch(setActiveFilter({ key, value: value.id }));
      } else if (value && value.id !== "all") {
        // For other dropdowns, store displayText
        dispatch(setActiveFilter({ key, value: value.displayText }));
      } else {
        dispatch(setActiveFilter({ key, value: null }));
      }
    }

    // Automatically apply filters after updating the filter state
    // Use setTimeout to ensure the Redux state is updated before building filters
    setTimeout(() => {
      // Update the local filtered list to reflect current filter selections
      dispatch(updateFilteredEmployeeList());

      // Build filters object with the updated filter (including the one just changed)
      const filters = buildFiltersObject();
      console.log("Auto-applying filters:", filters);

      // Fetch employees with updated filters
      if (Object.keys(filters).length > 0) {
        console.log("Making API call with filters:", filters);
        fetchFilteredEmployees(filters);
      } else {
        console.log("Making API call without filters (showing all employees)");
        fetchFilteredEmployees();
      }
    }, 0);
  };

  // Apply filters function - called when Apply Filters button is clicked
  const handleApplyFilters = () => {
    console.log("Applying filters:", activeFilters);

    // Check if location is selected before applying filters
    if (!selectedLocation?.id) {
      alert("Please select a location before applying filters");
      return;
    }

    // Update the local filtered list to reflect current filter selections
    dispatch(updateFilteredEmployeeList());

    // Build filters object from current active filters
    const filters = buildFiltersObject();
    console.log("Built API filters:", filters);

    // Fetch employees with updated filters
    if (Object.keys(filters).length > 0) {
      console.log("Making API call with filters:", filters);
      fetchFilteredEmployees(filters);
    } else {
      console.log("Making API call without filters (showing all employees)");
      fetchFilteredEmployees();
    }
  };

  // Clear all filters function
  const handleClearAllFilters = () => {
    console.log("Clearing all filters");

    // Check if location is selected
    if (!selectedLocation?.id) {
      alert("Please select a location before clearing filters");
      return;
    }

    // Clear all active filters first
    Object.keys(activeFilters).forEach((key) => {
      dispatch(setActiveFilter({ key, value: null }));
    });

    // Update the filtered list to show all employees
    dispatch(updateFilteredEmployeeList());

    // Clear all filters and fetch all employees
    fetchFilteredEmployees();
  };

  const isMobile = Dimensions.get("window").width < 768;
  
  // Helper to check if device is iPhone (not iPad)
  const isMobilePhone = () => {
    const windowWidth = Dimensions.get("window").width;
    return Platform.OS === "ios" && windowWidth < 768;
  };

  const handleDelete = async (
    id: number
  ): Promise<{ success: boolean; message: string }> => {
    try {
      // Ensure id is a valid number
      const numericId = Number(id);
      if (isNaN(numericId)) {
        console.error("Invalid ID provided to handleDelete:", id);
        return { success: false, message: "Invalid employee ID" };
      }

      // Enhanced logging for debugging
      console.log(
        "Attempting to delete employee with internal ID:",
        numericId,
        "Type:",
        typeof numericId
      );

      // Dispatch the deleteEmployee thunk with the numeric ID
      const result = await dispatch(deleteEmployee(numericId)).unwrap();

      // If the location is selected, refresh the employee list after deletion
      if (selectedLocation?.displayText && result.success) {
        dispatch(fetchEmployees({ location: selectedLocation.displayText }));
      }

      // Return the success result
      return {
        success: true,
        message: result.message || "Employee deleted successfully",
      };
    } catch (error: any) {
      // Handle error cases and return the error result
      let errorMessage = "Failed to delete employee";

      if (typeof error === "object" && error !== null) {
        errorMessage = error.message || errorMessage;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  };

  const handleEdit = (emp: any) => {
    try {
      // Use the utility function to create a clean copy without React elements
      const cleanEmp = createCleanEmployeeData(emp);
      dispatch(setEditEmployee(cleanEmp));
      dispatch(setShowOverlay(true));
    } catch (error) {
      console.error("Error in handleEdit:", error);
      // Show an error notification
      alert("Error preparing data for edit. Please try again.");
    }
  };

  const handleSaveEdit = async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    console.log("Saving edited employee:", editEmployee);

    if (!editEmployee) {
      return {
        success: false,
        message: "No employee data found to update.",
      };
    }

    try {
      // Ensure the employee has a valid numeric ID
      if (
        editEmployee.id === undefined ||
        editEmployee.id === null ||
        isNaN(Number(editEmployee.id))
      ) {
        console.error(
          "Invalid or missing employee ID in handleSaveEdit:",
          editEmployee.id
        );
        return {
          success: false,
          message: "Cannot update employee without a valid ID",
        };
      }

      // Ensure ID is a number and log for debugging
      const employeeWithNumericId = {
        ...editEmployee,
        id: Number(editEmployee.id),
      };

      console.log(
        "Updating employee with ID:",
        employeeWithNumericId.id,
        "Type:",
        typeof employeeWithNumericId.id
      );

      // Use the updateEmployee thunk to make the API call with the updated employee object
      const result = await dispatch(
        updateEmployee(employeeWithNumericId)
      ).unwrap();

      // The API call was successful
      console.log("Employee updated successfully:", result);

      // If the location is selected, refresh the employee list after update
      if (selectedLocation?.displayText) {
        dispatch(fetchEmployees({ location: selectedLocation.displayText }));
      }

      return {
        success: true,
        message: result.message || "Employee updated successfully!",
      };
    } catch (error: any) {
      // Handle error cases and return the error result
      console.error("Error updating employee:", error);
      let errorMessage = "Failed to update employee";

      if (typeof error === "object" && error !== null) {
        errorMessage = error.message || errorMessage;
      }

      return {
        success: false,
        message: errorMessage,
      };
    }
  };

  // Handler for toggling the New Hire status
  const handleToggleNewHire = async (id: number, newValue: boolean) => {
    // Clear any existing error messages
    setErrorMessage("");

    try {
      // Create API client
      const api = createApiClient();

      // Ensure ID is a number (consistent with other API calls in employeeUpdatesSlice.ts)
      const numericId = Number(id);

      // Build the URL with the employee ID as a number (same as in updateEmployee)
      const url = `/nho/employees/${numericId}`;
      console.log("Toggle New Hire request URL:", url);

      // Check if employeeList exists and is an array
      if (!employeeList || !Array.isArray(employeeList)) {
        throw new Error(
          "Employee list is not available. Please refresh the page and try again."
        );
      }

      // Find the employee in the employeeList to get all required fields
      const employee = employeeList.find((emp: Employee) => emp.id === id);

      if (!employee) {
        throw new Error("Employee not found in the current list.");
      }

      // Helper function to format dates to ISO format
      const formatDateToISO = (
        dateValue: string | null | undefined
      ): string | null => {
        if (!dateValue || dateValue === "N/A") {
          return null;
        }

        try {
          // Already a date-only string (YYYY-MM-DD)
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            return dateValue;
          }

          // Use local date parts to avoid UTC day-shift
          const toLocalDateString = (d: Date) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

          // Full ISO timestamp - parse then re-format using local date parts
          if (dateValue.includes("T")) {
            const parsed = new Date(dateValue);
            return !isNaN(parsed.getTime()) ? toLocalDateString(parsed) : dateValue.slice(0, 10);
          }

          // Try to parse mm/dd/yyyy format
          const dateParts = dateValue.split("/");
          if (dateParts.length === 3) {
            const month = parseInt(dateParts[0]) - 1; // JS months are 0-indexed
            const day = parseInt(dateParts[1]);
            const year = parseInt(dateParts[2]);

            const parsedDate = new Date(year, month, day);
            if (!isNaN(parsedDate.getTime())) {
              return toLocalDateString(parsedDate);
            }
          }

          return null;
        } catch (e) {
          console.error("Error formatting date:", e);
          return null;
        }
      };

      // Format graduation and hire dates
      const formattedGraduationDate = formatDateToISO(employee.graduationDate);
      const formattedHireDate = formatDateToISO(employee.hireDate);

      // Prepare complete request body according to the API schema
      const requestBody = {
        First_Name: employee.firstName,
        Last_Name: employee.lastName,
        GPID: employee.gpid,
        Status: employee.status,
        Job_Description: employee.jobDescription,
        Manager: employee.manager || "",
        Peer_Coach: "", // Optional field
        Expected_Graduation_Date: formattedGraduationDate,
        Hire_Date: formattedHireDate,
        New_Hire: newValue ? 1 : 0, // Convert boolean to 0/1 integer format expected by the API
        location: selectedLocation?.displayText || "",
      };

      console.log("Toggle New Hire request payload:", requestBody);

      // Make the PUT request with the complete payload
      await api.put(url, requestBody);

      // If API call is successful, update the local state
      console.log("new hire", newValue, "for employee ID:", id);
      dispatch({
        type: "employeeUpdates/updateEmployeeField",
        payload: { id, field: "newHire", value: newValue },
      });

      // Also update appData.employees store
      const employeesArray = Array.isArray(appDataEmployees) ? appDataEmployees : appDataEmployees?.data || [];
      if (employeesArray.length > 0) {
        const updatedAppDataEmployees = employeesArray.map((emp: any) => {
          if (emp.Id === id) {
            return {
              ...emp,
              New_Hire: newValue ? 1 : 0,
              Modified: new Date().toISOString(),
            };
          }
          return emp;
        });
        dispatch(setEmployees({ data: updatedAppDataEmployees }));
      }

      // No success notification needed as per requirements
    } catch (error: any) {
      // Enhanced error logging for debugging
      console.error("Error toggling New Hire status:", error);

      // Get detailed error information if available and show error notification bar
      const errorStatus = error.response?.status
        ? `${error.response.status}`
        : undefined;
      const apiErrorMessage = error.message || "Unknown error occurred";
      setErrorMessage(
        `Failed to update New Hire status. ${
          errorStatus ? `Server returned: ${errorStatus}` : apiErrorMessage
        }`
      );

      // Auto-clear error message after 5 seconds
      setTimeout(() => {
        setErrorMessage("");
      }, 5000);
    }
  };

  // Location check logic moved to the createEmployee action in the slice

  // Memoized helper functions to generate dynamic filter options from employeeUpdates slice
  const fullNameOptions = useMemo(() => {
    if (!employeeList || !Array.isArray(employeeList)) {
      return [];
    }

    // Create unique full names from employee data
    const uniqueNames = new Set();
    const nameOptions = employeeList
      .filter(
        (emp: Employee) => emp.firstName && emp.lastName && !emp.isDeleted
      )
      .map((emp: Employee) => {
        const fullName = `${emp.firstName} ${emp.lastName}`;
        if (!uniqueNames.has(fullName)) {
          uniqueNames.add(fullName);
          return {
            id:
              emp.id?.toString() ||
              `${emp.firstName}_${emp.lastName}`.toLowerCase(),
            displayText: fullName,
          };
        }
        return null;
      })
      .filter(
        (item): item is { id: string; displayText: string } => item !== null
      )
      .sort((a: any, b: any) => a.displayText.localeCompare(b.displayText));

    return nameOptions;
  }, [employeeList]);

  const gpidOptions = useMemo(() => {
    if (!employeeList || !Array.isArray(employeeList)) {
      return [];
    }

    // Create unique GPIDs from employee data
    const uniqueGpids = new Set();
    const gpidOptions = employeeList
      .filter((emp: Employee) => emp.gpid && !emp.isDeleted)
      .map((emp: Employee) => {
        if (!uniqueGpids.has(emp.gpid)) {
          uniqueGpids.add(emp.gpid);
          return {
            id: emp.id?.toString() || emp.gpid,
            displayText: emp.gpid,
          };
        }
        return null;
      })
      .filter(
        (item): item is { id: string; displayText: string } => item !== null
      )
      .sort((a: any, b: any) => a.displayText.localeCompare(b.displayText));

    return gpidOptions;
  }, [employeeList]);

  const jobDescriptionOptions = useMemo(() => {
    // Check if roles data exists as a flat array
    if (!appDataRoles || !Array.isArray(appDataRoles)) {
      console.log("No roles data found in appDataRoles:", appDataRoles);
      return [];
    }

    // Create job description options from roles data
    const roleOptions = appDataRoles
      .filter(
        (role: { ROLE_ID: number | string; ROLE_NAME: string }) =>
          role.ROLE_NAME && role.ROLE_ID
      )
      .map((role: { ROLE_ID: number | string; ROLE_NAME: string }) => ({
        id: role.ROLE_ID.toString(),
        displayText: role.ROLE_NAME,
      }))
      .sort((a: { displayText: string }, b: { displayText: string }) =>
        a.displayText.localeCompare(b.displayText)
      );

    console.log("Generated role options:", roleOptions);
    return roleOptions;
  }, [appDataRoles]);

  // Manager dropdown options from FieldUsers and COEUsers for EmployeeUpdatesHeader component
  const FieldUsers = useSelector((state: any) => state.appData.FieldUsers);
  const COEUsers = useSelector((state: any) => state.appData.COEUsers);

  // Generate manager dropdown options from Redux data for Header component
  const [headerManagerOptions, setHeaderManagerOptions] = useState<
    { id: string; displayText: string; email: string }[]
  >([]);

  // Update header manager options whenever FieldUsers or COEUsers change
  useEffect(() => {
    // Combine and process users from Redux store
    const options = [...(FieldUsers || []), ...(COEUsers || [])]
      .filter((u: any) => u.UserEmail)
      .map((u: any) => ({
        id: u.UserEmail, // id is email for backend
        displayText:
          u.Name && u.Name.trim().length > 0
            ? u.Name
            : formatUserName(u.UserEmail), // show parsed name in dropdown
        email: u.UserEmail, // email for backend
      }));

    setHeaderManagerOptions(options);
  }, [FieldUsers, COEUsers]);

  // Manager dropdown options from API results for LHFilter component
  const [filterManagerOptions, setFilterManagerOptions] = useState<
    { id: string; displayText: string; email: string }[]
  >([]);

  // Update filter manager options whenever employee list changes
  useEffect(() => {
    if (employeeList && Array.isArray(employeeList)) {
      // Extract unique manager emails from employee list
      const managerEmailMap = new Map<string, { name: string, email: string }>();
      
      employeeList.forEach((emp: Employee) => {
        if (emp.manager && emp.manager.trim() !== '') {
          // Use the manager field which contains the email
          const email = emp.manager;
          
          // Try to extract a display name from the email (before the @)
          let displayName = formatUserName(email);
          
          managerEmailMap.set(email, {
            name: displayName,
            email: email
          });
        }
      });
      
      // Convert the map to an array of options
      const options = Array.from(managerEmailMap.values()).map(manager => ({
        id: manager.email,
        displayText: manager.name,
        email: manager.email,
      }));
      
      // Sort by display name
      options.sort((a, b) => a.displayText.localeCompare(b.displayText));
      
      setFilterManagerOptions(options);
    }
  }, [employeeList]);

  // Use headerManagerOptions for the EmployeeUpdatesHeader
  const headerManagerNameOptions = useMemo(() => {
    return headerManagerOptions.length > 0 ? headerManagerOptions : [];
  }, [headerManagerOptions]);
  
  // Use filterManagerOptions for the LHFilter component
  const filterManagerNameOptions = useMemo(() => {
    return filterManagerOptions.length > 0 ? filterManagerOptions : [];
  }, [filterManagerOptions]);

  // Sync tempFilters with activeFilters when modal opens (following QualityAuditFilterModal pattern)
  useEffect(() => {
    if (showFilterModal) {
      console.log('[MOBILE FILTER DEBUG] Modal opened, syncing tempFilters with activeFilters:', activeFilters);
      setTempFilters(activeFilters);
    }
  }, [showFilterModal, activeFilters]);

  // Mobile filter handlers (following QualityAuditFilterModal pattern)
  const handleClose = () => {
    setTempFilters({});
    setShowFilterModal(false);
  };

  const handleTempFilterChange = (key: string, value: any) => {
    setTempFilters((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Special handler for MobileFilterOverlay that receives all filters at once
  // Following QualityAudit pattern: batch update filters, then apply like handleApplyFilters
  const handleMobileProceed = (selectedFilters: Partial<Record<string, any>>) => {
    console.log('[MOBILE FILTER DEBUG] Proceed with filters:', selectedFilters);
    
    // Check if location is selected before applying filters
    if (!selectedLocation?.id) {
      alert("Please select a location before applying filters");
      setShowFilterModal(false);
      return;
    }
    
    // Convert normalized values from MobileFilterOverlay to Redux activeFilters format
    // This follows the SAME logic as handleFilterChange to ensure consistency
    Object.entries(selectedFilters).forEach(([key, value]) => {
      console.log(`[MOBILE FILTER DEBUG] Processing filter ${key}:`, value);
      
      // Check if this is a date filter
      const isDateFilter = ['hireDate', 'graduationDate', 'lastObservation', 'lastPulseCheck'].includes(key);
      
      if (isDateFilter) {
        // Date filters: store direct string value (same as handleFilterChange)
        console.log(`[MOBILE FILTER DEBUG] Setting date filter ${key} to:`, value);
        dispatch(setActiveFilter({ key, value: value }));
      } else {
        // Dropdown filters: need to convert to proper format
        // Check for "all" or empty values first (these clear the filter)
        if (value === 'all' || value === null || value === '') {
          console.log(`[MOBILE FILTER DEBUG] Clearing filter ${key}`);
          dispatch(setActiveFilter({ key, value: null }));
        } else {
          // Find the matching option object from filterOptions
          const filter = filterOptions.find(f => f.key === key);
          
          if (filter && filter.options) {
            // Mobile filter values come as either id or displayText strings
            // Find the matching option object
            const matchingOption = filter.options.find(opt => 
              String(opt.id) === String(value) || opt.displayText === value
            );
            
            if (matchingOption) {
              // Store in Redux following handleFilterChange logic:
              // - manager: store id (email)
              // - others: store displayText
              if (key === "manager") {
                console.log(`[MOBILE FILTER DEBUG] Setting manager filter to id:`, matchingOption.id);
                dispatch(setActiveFilter({ key, value: matchingOption.id }));
              } else {
                console.log(`[MOBILE FILTER DEBUG] Setting ${key} filter to displayText:`, matchingOption.displayText);
                dispatch(setActiveFilter({ key, value: matchingOption.displayText }));
              }
            } else {
              console.warn('[MOBILE FILTER DEBUG] No matching option found for:', key, value);
              // Still set the value directly if no match found (fallback)
              dispatch(setActiveFilter({ key, value: value }));
            }
          } else {
            console.warn('[MOBILE FILTER DEBUG] No filter config found for:', key);
            // Set value directly if filter config not found
            dispatch(setActiveFilter({ key, value: value }));
          }
        }
      }
    });
    
    // Now apply filters using the same logic as handleApplyFilters
    // Build filters directly from selectedFilters instead of waiting for Redux update
    console.log('[MOBILE FILTER DEBUG] Building API filters directly from selectedFilters');
    
    const filters: Record<string, string> = {};
    
    Object.entries(selectedFilters).forEach(([filterKey, filterValue]) => {
      // Skip null, undefined, or empty string values
      if (filterValue === null || filterValue === undefined || filterValue === '' || filterValue === 'all') {
        console.log(`[MOBILE FILTER DEBUG] Skipping ${filterKey} - empty or "all"`);
        return;
      }
      
      console.log(`[MOBILE FILTER DEBUG] Building API param for ${filterKey}:`, filterValue);
      
      // Handle Full Name filter - split into First_Name and Last_Name
      if (filterKey === "fullName") {
        // Split the full name string
        const nameParts = String(filterValue).trim().split(/\s+/);
        if (nameParts.length >= 2) {
          filters.First_Name = nameParts[0];
          filters.Last_Name = nameParts.slice(1).join(' ');
          console.log(`[MOBILE FILTER DEBUG] Split fullName into First_Name: ${filters.First_Name}, Last_Name: ${filters.Last_Name}`);
        }
      }
      // Handle Job Description filter - map to Role
      else if (filterKey === "jobDescription") {
        const selectedRole = appDataRoles?.find(
          (role: { ROLE_ID: number | string; ROLE_NAME: string }) =>
            role.ROLE_NAME === filterValue || role.ROLE_ID.toString() === filterValue
        );
        if (selectedRole) {
          filters.Role = selectedRole.ROLE_NAME;
          console.log(`[MOBILE FILTER DEBUG] Mapped jobDescription to Role:`, filters.Role);
        }
      }
      // Handle GPID filter
      else if (filterKey === "gpid") {
        filters.GPID = String(filterValue);
        console.log(`[MOBILE FILTER DEBUG] Added GPID:`, filters.GPID);
      }
      // Handle Status filter
      else if (filterKey === "status") {
        filters.Active_vs_Inactive = String(filterValue);
        console.log(`[MOBILE FILTER DEBUG] Added Active_vs_Inactive:`, filters.Active_vs_Inactive);
      }
      // Handle Manager filter
      else if (filterKey === "manager") {
        filters.Manager = String(filterValue);
        console.log(`[MOBILE FILTER DEBUG] Added Manager:`, filters.Manager);
      }
    });
    
    console.log('[MOBILE FILTER DEBUG] Final built API filters:', filters);
    
    // Update the local filtered list to reflect current filter selections
    dispatch(updateFilteredEmployeeList());
    
    // Fetch employees with updated filters
    if (Object.keys(filters).length > 0) {
      console.log('[MOBILE FILTER DEBUG] Making API call with filters:', filters);
      fetchFilteredEmployees(filters);
    } else {
      console.log('[MOBILE FILTER DEBUG] Making API call without filters (showing all)');
      fetchFilteredEmployees();
    }
    
    // Close the modal after filters are applied
    setShowFilterModal(false);
  };

  // Updated filter options with dynamic data
  const filterOptions: FilterItem[] = useMemo(
    () => {
      const baseFilters = [
        {
          key: "fullName",
          label: "Full Name",
          options: fullNameOptions,
        },
        {
          key: "gpid",
          label: "GPID",
          options: gpidOptions,
        },
        {
          key: "status",
          label: "Status",
          options: [
            { id: "active", displayText: "Active" },
            { id: "inactive", displayText: "Inactive" },
            { id: "terminated", displayText: "Terminated" },
          ],
        },
        {
          key: "jobDescription",
          label: "Job Description",
          options: jobDescriptionOptions,
        },
        {
          key: "manager",
          label: "Manager Name",
          options: filterManagerNameOptions,
          getValueForBackend: (selectedId: string) => {
            // Find the selected option and return its email
            const found = filterManagerNameOptions.find((opt) => opt.id === selectedId);
            return found?.email || "";
          },
        },
      ];

      // Add selectedOptionId to each filter based on activeFilters
      return baseFilters.map((filter) => {
        const activeValue = activeFilters[filter.key];
        let selectedOptionId = "";

        if (activeValue) {
          // For manager filter, activeValue is the email (id)
          if (filter.key === "manager") {
            selectedOptionId = activeValue;
          } else {
            // For other filters, find the matching option by displayText
            const matchingOption = filter.options?.find(
              (opt) => opt.displayText === activeValue
            );
            selectedOptionId = matchingOption?.id?.toString() || "";
          }
        }

        return {
          ...filter,
          selectedOptionId,
        };
      });
    },
    [fullNameOptions, gpidOptions, jobDescriptionOptions, filterManagerNameOptions, activeFilters]
  );

  // Debug filter options changes
  useEffect(() => {
    console.log("Full Name options (from employeeList):", fullNameOptions);
    console.log("GPID options (from employeeList):", gpidOptions);
    console.log(
      "Job Description options (from appDataRoles):",
      jobDescriptionOptions
    );
    console.log("Current employeeList:", employeeList);
  }, [fullNameOptions, gpidOptions, jobDescriptionOptions, employeeList]);

  const columns = [
    { columnKey: "firstName", header: "First Name" },
    { columnKey: "lastName", header: "Last Name" },
    { columnKey: "gpid", header: "GPID" },
    { columnKey: "manager", header: "Manager" },
    { columnKey: "status", header: "Status" },
    { columnKey: "jobDescription", header: "Job Description" },
    {
      columnKey: "lastObservation",
      header: "Last Observation",
      renderCell: (item: any) => {
        const value = item.lastObservation;
        if (value === "N/A") {
          return <Text style={{ color: isDarkTheme ? colors.text.secondary : '#888' }}>{value}</Text>;
        }
        let color = isDarkTheme ? colors.text.primary : '#222';
        let daysDiff = null;
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const today = new Date();
            // Zero out time for both dates
            date.setHours(0,0,0,0);
            today.setHours(0,0,0,0);
            const diffTime = today.getTime() - date.getTime();
            daysDiff = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            if (daysDiff > 7) color = isDarkTheme ? '#FF6B6B' : 'red';
            else if (daysDiff > 5) color = isDarkTheme ? '#FFB020' : 'orange';
            else color = isDarkTheme ? '#4ADE80' : 'green';
          }
        } catch (e) {}
        return <Text style={{ color }} {...({ dataSet: { lhKeepColor: '1' } } as any)}>{value}</Text>;
      },
    },
    {
      columnKey: "lastPulseCheck",
      header: "Last Pulse Check",
      renderCell: (item: any) => {
        const value = item.lastPulseCheck;
        if (value === "N/A") {
          return <Text style={{ color: isDarkTheme ? colors.text.secondary : '#888' }}>{value}</Text>;
        }
        let color = isDarkTheme ? colors.text.primary : '#222';
        let daysDiff = null;
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const today = new Date();
            date.setHours(0,0,0,0);
            today.setHours(0,0,0,0);
            const diffTime = today.getTime() - date.getTime();
            daysDiff = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            if (daysDiff > 7) color = isDarkTheme ? '#FF6B6B' : 'red';
            else if (daysDiff > 5) color = isDarkTheme ? '#FFB020' : 'orange';
            else color = isDarkTheme ? '#4ADE80' : 'green';
          }
        } catch (e) {}
        return <Text style={{ color }} {...({ dataSet: { lhKeepColor: '1' } } as any)}>{value}</Text>;
      },
    },
    { columnKey: "hireDate", header: "Hire Date" },
    { columnKey: "graduationDate", header: "Graduation Date" },
    {
      columnKey: "newHire",
      header: "New Hire",
      renderCell: (item: any) => (
        <LhToggleSwitch
          value={!!item.newHire}
          onValueChange={(val) => {
            // Make API call to update the New Hire status
            if (item.id) {
              handleToggleNewHire(item.id, val);
            }
          }}
        />
      ),
    },
    // { columnKey: "actions", header: "Actions" },
  ];

  if (!employeeList || !Array.isArray(employeeList)) {
    return null;
  }

  // Function to prepare table data with UI formatting and actions
  const prepareTableData = (employees: Employee[] = []) => {
    return employees.map((emp: Employee) => {
      return {
        ...emp,
        actions: (
          <View style={{ flexDirection: "row", gap: 12 }}>
            <TouchableOpacity onPress={() => handleEdit(emp)}>
              <MaterialIcons name="edit" size={20} color="#007bff" />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => handleDelete(emp.id)}>
              <MaterialIcons name="delete" size={20} color="#dc3545" />
            </TouchableOpacity>
          </View>
        ),
      };
    });
  };

  // Default to empty array if filteredEmployeeList is undefined
  const displayData = filteredEmployeeList
    ? prepareTableData(filteredEmployeeList)
    : [];

  // Components for different table states are implemented as separate files

  // Return null if employee list is invalid
  if (!employeeList || !Array.isArray(employeeList)) {
    return null;
  }

  // Show filter modal for iPhone only - Following QualityAuditFilterModal pattern
  if (showFilterModal && isMobileIos) {
    return (
      <MobileFilterOverlay
        visible={showFilterModal}
        onDismiss={handleClose}
        filters={filterOptions}
        onFilterChange={handleTempFilterChange}
        onProceed={handleMobileProceed}
        onCancel={handleClose}
        currentFilters={tempFilters}
      />
    );
  }

  // Show edit employee overlay full-screen on iPhone only
  if (showOverlay && isMobileIos) {
    return (
      <EmployeeEditOverlay
        showOverlay={showOverlay}
        setShowOverlay={(value) => dispatch(setShowOverlay(value))}
        editEmployee={editEmployee}
        setEditEmployee={(emp) => dispatch(setEditEmployee(emp))}
        statusOptions={statusOptions}
        managerOptions={headerManagerNameOptions}
        handleDelete={handleDelete}
        handleSaveEdit={handleSaveEdit}
      />
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1, backgroundColor: isDarkTheme ? colors.background.page : undefined }}
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={[
          styles.container,
          isDarkTheme && { backgroundColor: colors.background.page },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={true}
      >
        {onBackPress && (
          <AdminBreadcrumb
            items={[
              { label: 'Configuration Module', onPress: onBackPress },
              { label: 'Employee Updates' },
            ]}
          />
        )}
        <EmployeeUpdatesHeader
          onBackPress={onBackPress}
          statusOptions={statusOptions}
          managerOptions={headerManagerNameOptions}
          selectedLocation={selectedLocation}
          onOverlayVisibilityChange={setIsAddEmployeeOverlayVisible}
        />

        {/* Success Message Notification */}
        {successMessage && (
          <View
            style={[
              styles.successMessageContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
          >
            <MaterialIcons
              name="check-circle"
              size={24}
              color={isDarkTheme ? colors.text.primary : "#008242"}
              style={styles.successIcon}
            />
            <Text
              style={[
                styles.successMessageText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              {successMessage}
            </Text>
          </View>
        )}

        {/* Error Message Notification Bar */}
        {errorMessage && (
          <View
            style={[
              styles.errorMessageContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderColor: colors.border.default,
              },
            ]}
          >
            <MaterialIcons
              name="error"
              size={24}
              color={isDarkTheme ? colors.text.primary : "#d32f2f"}
              style={styles.errorIcon}
            />
            <Text
              style={[
                styles.errorMessageText,
                isDarkTheme && { color: colors.text.primary },
              ]}
            >
              {errorMessage}
            </Text>
          </View>
        )}

        {/* Main Content Container with White Background - Hidden on iPhone when overlay is visible */}
        {!(isMobilePhone() && isAddEmployeeOverlayVisible) && (
          <View
            style={[
              styles.contentContainer,
              isDarkTheme && {
                backgroundColor: isMobileIos
                  ? colors.background.page
                  : colors.background.surface,
              },
            ]}
          >
            {/* Header */}
            <View style={styles.contentHeader}>
              <Text
                style={[
                  styles.contentHeaderText,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                Employee List
              </Text>
              {isMobileIos && (
                <TouchableOpacity
                  style={[
                    styles.mobileFilterButton,
                    isDarkTheme && { backgroundColor: colors.brand.primary },
                  ]}
                  onPress={() => setShowFilterModal(true)}
                >
                  <MaterialIcons name="filter-list" size={16} color="#FFFFFF" />
                  <Text style={styles.mobileFilterButtonText}>Filters</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Toggle Switches */}
            <ToggleSwitches
              showPeriodObservation={showPeriodObservation}
              setShowPeriodObservation={(value: boolean) => dispatch(setShowPeriodObservation(value))}
              showWeeklyPulseCheck={showWeeklyPulseCheck}
              setShowWeeklyPulseCheck={(value: boolean) => dispatch(setShowWeeklyPulseCheck(value))}
              isMobile={isMobile}
            />

          {/* Filters - Auto-apply on selection */}
          <View style={[styles.filterContainer, { padding: 20 }] }>
            {!selectedLocation?.id && (
              <View
                style={[
                  styles.locationWarning,
                  isDarkTheme && {
                    backgroundColor: colors.background.elevated,
                    borderColor: colors.border.default,
                  },
                ]}
              >
                <Text style={[styles.locationWarningText, isDarkTheme && { color: colors.text.primary }]}>
                  <MaterialIcons name="warning" size={16} color={isDarkTheme ? colors.text.primary : "#856404"} />
                  Please select a location before using filters
                </Text>
              </View>
            )}
            {!isMobileIos && (
              <LHFilter
                filters={filterOptions}
                onFilterChange={handleFilterChange}
                itemsPerRow={isMobile ? 1 : 4}
                showActionButton={false}
                onClearAllFilters={handleClearAllFilters}
              />
            )}
          </View>

          {/* Data Table Section - Updated to show filtered data with location selection and loading state */}
          <View
            style={[
              styles.dataTableContainer,
              isDarkTheme && {
                backgroundColor: isMobileIos
                  ? colors.background.page
                  : colors.background.elevated,
              },
            ]}
          >
            {!selectedLocation?.id ? (
              <LocationRequiredMessage />
            ) : loading ? (
              <LoadingMessage />
            ) : displayData.length === 0 ? (
              <NoResultsMessage />
            ) : isMobileIos ? (
              // Card layout for iOS mobile
              <View style={styles.cardsContainer}>
                {(() => {
                  const paginatedData = displayData.slice(
                    (currentPage - 1) * rowsPerPage,
                    currentPage * rowsPerPage
                  );
                  return paginatedData.map((item, index) => (
                    <React.Fragment key={`card-${item.id || index}`}>
                      <EmployeeCardItem
                        item={item}
                        columns={columns}
                        onRowClick={handleEdit}
                        onToggleNewHire={handleToggleNewHire}
                      />
                      {index < paginatedData.length - 1 && <CardSeparator />}
                    </React.Fragment>
                  ));
                })()}

                {/* Mobile pagination controls */}
                <View style={styles.mobilePaginationContainer}>
                  <View
                    style={[
                      styles.mobilePaginationBox,
                      isDarkTheme && { borderColor: colors.border.default },
                    ]}
                  >
                    <TouchableOpacity
                      disabled={displayStart === 1 && currentPage === 1}
                      onPress={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        setDisplayStart(Math.max(1, newPage));
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart === 1 &&
                          currentPage === 1 &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-prev-page-button"
                      accessibilityLabel="Previous page"
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={16}
                        color={
                          displayStart === 1 && currentPage === 1
                            ? isDarkTheme
                              ? colors.text.secondary
                              : "#A0A0A0"
                            : isDarkTheme
                            ? colors.text.primary
                            : "#005CBC"
                        }
                      />
                    </TouchableOpacity>

                    {(() => {
                      const a = displayStart;
                      const b = Math.min(totalPages, displayStart + 1);
                      const aIsActive = currentPage === a;
                      const bIsActive = currentPage === b;
                      const isSinglePage = totalPages === 1;

                      return (
                        <>
                          <TouchableOpacity
                            disabled={a > totalPages}
                            onPress={() => a <= totalPages && setCurrentPage(a)}
                            accessibilityLabel={`Go to page ${a}`}
                            style={[
                              styles.mobilePaginationCell,
                              aIsActive
                                ? styles.mobilePaginationCurrentCell
                                : styles.mobilePaginationNextCell,
                              isDarkTheme &&
                                !aIsActive && { backgroundColor: colors.background.elevated },
                            ]}
                          >
                            <Text
                              style={[
                                aIsActive
                                  ? styles.mobilePaginationCurrentText
                                  : styles.mobilePaginationNextText,
                                isDarkTheme &&
                                  !aIsActive && { color: colors.text.primary },
                              ]}
                            >
                              {a}
                            </Text>
                          </TouchableOpacity>

                          {isSinglePage ? (
                            <View
                              style={[
                                styles.mobilePaginationCell,
                                styles.mobilePaginationEmptyCell,
                                isDarkTheme && { backgroundColor: colors.background.elevated },
                              ]}
                            />
                          ) : (
                            <TouchableOpacity
                              disabled={b > totalPages}
                              onPress={() =>
                                b <= totalPages && setCurrentPage(b)
                              }
                              accessibilityLabel={`Go to page ${b}`}
                              style={[
                                styles.mobilePaginationCell,
                                bIsActive
                                  ? styles.mobilePaginationCurrentCell
                                  : styles.mobilePaginationNextCell,
                                isDarkTheme &&
                                  !bIsActive && { backgroundColor: colors.background.elevated },
                              ]}
                            >
                              <Text
                                style={[
                                  bIsActive
                                    ? styles.mobilePaginationCurrentText
                                    : styles.mobilePaginationNextText,
                                  isDarkTheme &&
                                    !bIsActive && { color: colors.text.primary },
                                ]}
                              >
                                {b}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </>
                      );
                    })()}

                    <TouchableOpacity
                      disabled={
                        displayStart + 1 >= totalPages &&
                        currentPage === totalPages
                      }
                      onPress={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        setCurrentPage(newPage);
                        setDisplayStart(newPage);
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart + 1 >= totalPages &&
                          currentPage === totalPages &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-next-page-button"
                      accessibilityLabel="Next page"
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={16}
                        color={
                          displayStart + 1 >= totalPages &&
                          currentPage === totalPages
                            ? isDarkTheme
                              ? colors.text.secondary
                              : "#A0A0A0"
                            : isDarkTheme
                            ? colors.text.primary
                            : "#005CBC"
                        }
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            ) : (
              // Table layout for web and iPad
              <LHDataTable
                data={displayData}
                columns={columns}
                maxRecordsToShow={10000} // Setting a very large value to effectively disable pagination
                tableHeader=""
                tableDescription={`Showing ${displayData.length} of ${
                  employeeList.filter((emp) => !emp.isDeleted).length
                } result(s)`}
                onRowClick={handleEdit}
              />
            )}
          </View>
        </View>
        )}

        {/* Overlay for editing employee - Only render for non-iPhone devices (iPad/Web use LHOverlay) */}
        {!isMobileIos && (
          <EmployeeEditOverlay
            showOverlay={showOverlay}
            setShowOverlay={(value) => dispatch(setShowOverlay(value))}
            editEmployee={editEmployee}
            setEditEmployee={(emp) => dispatch(setEditEmployee(emp))}
            statusOptions={statusOptions}
            managerOptions={headerManagerNameOptions}
            handleDelete={handleDelete}
            handleSaveEdit={handleSaveEdit}
          />
        )}
      </ScrollView>

      {/* Notification bars are now handled inline within the component */}
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: Platform.OS === "web" ? "2%" : 16,
    backgroundColor: "#F5F9FF",
    paddingVertical: "2%",
    minHeight: "100%",
  },
  contentContainer: {
    width: "100%",
    marginTop: 0,
    backgroundColor: "white",
    padding: 20,
    borderRadius: 8,
  },
  contentHeader: {
    marginBottom: 16,
    backgroundColor: "transparent",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  contentHeaderText: {
    overflow: "hidden",
    color: "#2D2D2D",
    fontFamily: FontFamily.bold,
    fontSize: 24,
    fontStyle: "normal",
    fontWeight: "700",
    lineHeight: 32,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 15,
    paddingHorizontal: 10,
  },
  removeButton: {
    backgroundColor: "red",
    minWidth: 120,
  },
  updateButton: {
    backgroundColor: "green",
    minWidth: 120,
  },
  buttonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
  },
  checkboxContainer: {
    padding: 16,
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
    gap: 16,
    marginBottom: 10,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  checkboxLabel: {
    color: "#343a40",
  },
  switchContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    textAlign: "center",
  },
  dataTableContainer: {
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow:
          "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
        background:
          " #FFF",
        borderRadius:
          " 8",
      },
    }),
    backgroundColor: "#fff",
    borderRadius: 8,
    // marginTop: 8,
   padding: 0,
  },
  // Styles moved to EmployeeUpdatesHeader component
  formSection: {
    marginBottom: 20,
  },
  sectionHeader: {
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
    paddingHorizontal: 10,
    color: "#333",
  },
  sectiontableHeader: {
    backgroundColor: "#0066CC",
    padding: 16,
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 5,
  },
  textHeader: {
    color: "#fff",
    fontSize: Platform.select({ web: 16, default: 14 }),
    fontWeight: "bold",
    textAlign: "center",
  },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: Platform.select({ web: 10, default: 8 }),
    marginBottom: 10,
    marginHorizontal: Platform.select({ web: 4, default: 0 }),
    fontSize: Platform.select({ web: 16, default: 14 }),
  },
  inputGroup: {
    flex: 1,
    marginHorizontal: 4,
    // marginVertical: 16, // Increased vertical margin
    padding: 12, // Added padding for better spacing
    backgroundColor: "#fff", // Optional: subtle background for separation
    borderRadius: 8, // Optional: rounded corners for better look
  },
  dropdownContainer: {
    marginBottom: 10,
  },
  addButton: {
    backgroundColor: "green",
    padding: 12,
    borderRadius: 20,
    alignItems: "center",
    marginTop: 15,
    marginLeft: Platform.select({ web: 600, default: 0 }),
    marginRight: Platform.select({ web: 600, default: 0 }),
  },
  mobileAddButton: {
    width: Platform.OS === "web" ? "50%" : "50%",
    alignSelf: "center",
    marginLeft: 0,
    marginRight: 0,
  },
  addButtonText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: Platform.select({ web: 16, default: 14 }),
  },
  divider: {
    height: 1,
    backgroundColor: "#ddd",
    marginVertical: 10,
  },
  filterContainer: {
    marginBottom: 0,
    position: "relative", // Ensure stacking context
    zIndex: 10010, // Lower than date picker wrapper
    ...Platform.select({
      web: {
        overflow: "visible",
      },
    }),
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  // Switch styles removed as they are now in the ToggleSwitches component
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#fff",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
  },
  returnButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#0066CC",
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 6,
  },
  returnButtonIcon: {
    color: "white",
    fontSize: 16,
    marginRight: 4,
  },
  returnButtonText: {
    color: "white",
    fontSize: 14,
    fontWeight: "500",
  },
  // Button styles moved to EmployeeUpdatesHeader component
  filterDatePickerWrapper: {
    marginBottom: 10,
    marginTop: 10,
    position: "relative",
    zIndex: 20000, // Even higher to ensure calendar popover is above filter buttons
    overflow: "visible",
  },
  locationWarning: {
    backgroundColor: "#fff3cd",
    borderColor: "#ffeeba",
    borderWidth: 1,
    borderRadius: 4,
    padding: 10,
    marginBottom: 10,
    flexDirection: "row",
    alignItems: "center",
  },
  locationWarningText: {
    color: "#856404",
    fontSize: 14,
    fontWeight: "500",
  },
  successMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginTop: 16,
  },
  successIcon: {
    marginRight: 8,
  },
  successMessageText: {
    color: "#155724",
    fontSize: 16,
    flex: 1,
  },
  errorMessageContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderWidth: 1,
    borderRadius: 6,
    padding: 16,
    marginBottom: 16,
    marginTop: 16,
  },
  errorIcon: {
    marginRight: 8,
  },
  errorMessageText: {
    color: "#721c24",
    fontSize: 16,
    flex: 1,
  },
  // Card styles for iOS mobile
  cardsContainer: {
    backgroundColor: "transparent",
  },
  // Mobile pagination styles
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationEmptyCell: {
    backgroundColor: "#FBFCFE",
    opacity: 0.3,
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#005CBC",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 24,
    gap: 4,
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
    fontFamily: FontFamily.regular,
  },
});

export default ManageEmployeeUpdates;
